import { FC } from 'react';
import { IonText } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import dayjs from 'dayjs';
import { CalendarDayRegularIcon, ChevronRightIcon } from '../../theme/Icons';
import { IAppointment } from '../../appointments/IAppointment';

const useStyles = createUseStyles({
    appointmentLink: {
        display: 'flex',
        alignItems: 'center',
        padding: '12px 0',
        cursor: 'pointer',
        gap: 12,

        '&:hover': {
            backgroundColor: 'var(--ion-color-step-50, #f7f7f7)',
            margin: '0 -16px',
            padding: '12px 16px'
        }
    },

    iconWrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
    },

    appointmentDetails: {
        flex: 1
    },

    dateTime: {
        fontSize: 15,
        fontWeight: 500,
        color: 'var(--ion-color-step-600, #666666)',
        margin: 0,
        marginBottom: 2,
        marginRight: 8,
        whiteSpace: 'nowrap'
    },

    appointmentTitle: {
        fontSize: 15,
        fontWeight: 600,
        color: 'var(--ion-color-dark, #222428)',
        margin: 0,
        lineHeight: 1.2
    },

    chevronWrapper: {
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0
    }
});

interface IAppointmentLinkProps {
    appointment: IAppointment;
    onClick: () => void;
}

export const AppointmentLink: FC<IAppointmentLinkProps> = ({ appointment, onClick }) => {
    const classes = useStyles();

    // Format date similar to screenshot: "Thur Jun 17 1PM"
    const formatDateTime = (start: string) => {
        const startDate = dayjs(start);
        return startDate.format('ddd MMM D h:mmA');
    };

    // Format title with provider if available
    const getDisplayTitle = () => {
        const { title } = appointment;
        // In the screenshot it shows "Cardiologist | Dr. Patel" format
        // For now, just return the title. In a real implementation,
        // you might want to look up the support member name and format as "title | provider"
        return title;
    };

    return (
        <div
            className={classes.appointmentLink}
            onClick={onClick}
            data-type="appointment-link"
            data-appointment-id={appointment.appointmentId}
        >
            <div className={classes.iconWrapper}>
                <CalendarDayRegularIcon color="medium" size="medium" />
            </div>

            <div className={classes.appointmentDetails}>
                <IonText className={classes.dateTime}>
                    {formatDateTime(appointment.start)}
                </IonText>
                <IonText className={classes.appointmentTitle}>
                    {getDisplayTitle()}
                </IonText>
            </div>

            <div className={classes.chevronWrapper}>
                <ChevronRightIcon color="medium" size="small" />
            </div>
        </div>
    );
};
